import { useState, useEffect } from 'react';
import { useCodeMirror } from '../../hooks/useCodeMirror';
import { useDebounce } from '../../hooks/useDebounce';
import { useFountainParser } from '../../hooks/useFountainParser';
import FountainPreview from './FountainPreview';
import EditorToolbar from './EditorToolbar';

const ScriptEditor = ({
  initialText = '',
  onTextChange,
  onAnalyze,
  isAnalyzing = false
}) => {
  const [editorText, setEditorText] = useState(initialText);
  const [viewMode, setViewMode] = useState('split'); // 'editor', 'preview', 'split'

  // Debounce for auto-analysis
  const debouncedText = useDebounce(editorText, 1500);

  // Parse Fountain
  const fountainData = useFountainParser(editorText);

  // Initialize CodeMirror
  const { editorRef } = useCodeMirror(initialText, (newText) => {
    setEditorText(newText);
    onTextChange(newText);
  });

  // Trigger analysis when text stabilizes
  useEffect(() => {
    if (debouncedText && debouncedText !== initialText && debouncedText.trim().length > 0) {
      onAnalyze(debouncedText);
    }
  }, [debouncedText]);

  const exportToFountain = () => {
    const blob = new Blob([editorText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `screenplay-${Date.now()}.fountain`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full">
      <EditorToolbar
        viewMode={viewMode}
        setViewMode={setViewMode}
        onExport={exportToFountain}
        isAnalyzing={isAnalyzing}
      />

      <div className="flex-1 flex overflow-hidden">
        {/* Editor Pane - Always mounted, visibility controlled by CSS */}
        <div
          className={`${
            viewMode === 'split' ? 'w-1/2' : viewMode === 'editor' ? 'w-full' : 'hidden'
          } border-r border-slate-200 overflow-auto`}
        >
          <div ref={editorRef} className="h-full" />
        </div>

        {/* Preview Pane */}
        <div
          className={`${
            viewMode === 'split' ? 'w-1/2' : viewMode === 'preview' ? 'w-full' : 'hidden'
          } overflow-auto bg-slate-50 p-8`}
        >
          <FountainPreview fountainData={fountainData} />
        </div>
      </div>
    </div>
  );
};

export default ScriptEditor;
